/**
 * Agent 工具调用的产品化文案映射。
 *
 * 工具在 schema 里用英文蛇形名，这些名会出现在两处前端渲染：
 *  - agent-activity.tsx：实时进度（executing 时「正在调用 xxx…」）
 *  - agent-message.tsx：历史步骤卡片（toolLabel + summarizeInput）
 * 两处都需把原始名 + input 转成中文，故抽成共享模块统一维护，避免文案漂移。
 */

export const TOOL_LABELS: Record<string, string> = {
  daily_hotlist: "查询日榜",
  keyword_count: "统计次数",
  keyword_trend: "热度趋势",
  topic_search: "搜索词条",
  topic_detail: "词条详情",
  topic_compare: "词条对比",
  ranking_snapshot: "动态榜单",
  celebrity_ranking: "明星排行",
  entity_ranking: "地区热搜",
  time_distribution: "时段分布",
  subscription_confirm: "订阅词条",
  click_element_by_index: "点击元素",
  scroll: "滚动页面",
  done: "完成",
  ask_user: "询问用户",
};

export function toolLabel(name: string): string {
  return TOOL_LABELS[name] || name;
}

/** 工具入参的产品化摘要（如「日期 2026-06-26」「关键词「C罗」」）。无摘要返回空串。 */
export function summarizeInput(
  name: string,
  input?: Record<string, unknown> | null
): string {
  if (!input) return "";
  if (name === "daily_hotlist") return `日期 ${input.date ?? ""}`;
  if (name === "keyword_count") {
    if (input.startDate && input.endDate) return `关键词「${input.keyword ?? ""}」 ${input.startDate}~${input.endDate}`;
    return `关键词「${input.keyword ?? ""}」 ${input.year ?? ""}年`;
  }
  if (name === "keyword_trend") return `关键词「${input.keyword ?? ""}」 最近${input.days ?? 7}天`;
  if (name === "topic_search") return `关键词「${input.keyword ?? ""}」`;
  if (name === "topic_detail") return `关键词「${input.keyword ?? ""}」`;
  if (name === "topic_compare") {
    const kws = input.keywords as string[] | undefined;
    return kws ? kws.join(" vs ") : "";
  }
  if (name === "ranking_snapshot") return `${input.type ?? ""}榜`;
  if (name === "celebrity_ranking") return `${input.category ?? "全部"}明星`;
  if (name === "entity_ranking") return `${input.entityName ?? ""}`;
  if (name === "time_distribution") return `${input.type ?? ""}分布`;
  if (name === "subscription_confirm") return `订阅「${input.keyword ?? ""}」`;
  if (name === "click_element_by_index") return `元素 #${input.index ?? ""}`;
  if (name === "scroll") return `${input.direction ?? ""} ${input.num_pages ?? ""}页`;
  return "";
}

/**
 * 实时调用进度文案：把原始工具名 + 入参拼成一句自然中文。
 */
export function describeToolExecuting(
  tool?: string,
  input?: Record<string, unknown> | null
): string {
  const label = tool ? toolLabel(tool) : "工具";
  const summary = tool ? summarizeInput(tool, input) : "";
  const VERB: Record<string, string> = {
    daily_hotlist: "正在查询",
    keyword_count: "正在统计",
    keyword_trend: "正在查询",
    topic_search: "正在搜索",
    topic_detail: "正在查询",
    topic_compare: "正在对比",
    ranking_snapshot: "正在查询",
    celebrity_ranking: "正在查询",
    entity_ranking: "正在查询",
    time_distribution: "正在分析",
    subscription_confirm: "正在订阅",
    click_element_by_index: "正在点击",
    scroll: "正在滚动",
    ask_user: "正在询问",
  };
  const verb = (tool && VERB[tool]) || "正在调用";
  if (tool === "keyword_count") return `${verb}${summary}的热搜次数…`;
  if (tool === "keyword_trend") return `${verb}${summary}的热度趋势…`;
  if (tool === "topic_compare") return `${verb} ${summary} 的热度…`;
  if (tool === "subscription_confirm") return `${verb}${summary}…`;
  if (summary) return `${verb} ${label} · ${summary}…`;
  return `${verb} ${label}…`;
}
